import {useTranslation} from "react-i18next";
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import {ApiResponseDto, BasePopupProps} from "@utils/Models";
import {useMemo, useState} from "react";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import FlintButton from "@components/controls/FlintButton";
import FlintTextBox from "@components/controls/FlintTextBox";
import {
    BaseProductCodeListItemModel,
    EmeaProductCodeListItemModel,
    NaProductCodeListItemModel
} from "@pages/product_codes/productcode.models";
import axios from "axios";
import {ApiUrl_ProductCodes_SaveListItem} from "@utils/Endpoints";
import {useFlintNotificationContext} from "@components/page_components/common/notifications/FlintNotificationContext";
import {RadioGroup} from "@progress/kendo-react-inputs";
import {VivoPortal} from "@utils/AppConstants";
import EnumProductCodeListTypes_Emea = VivoPortal.EnumProductCodeListTypes_Emea;
import EnumProductCodeType = VivoPortal.EnumProductCodeType;


export default function EditProductCodeListItemComponent(props: EditProductCodeListItemComponentProps) {
    const {t} = useTranslation();
    const {state, utils, handlers} = useEditProductCodeListItemComponent(props);

    return (
        <FlintLoadingWrapper showLoading={state.isLoading.get}>
            <div className={"dialog-content-wrapper"}>
                <div>
                    <div className={"h5 text-center"}>{t(utils.labelKey)}</div>
                    <div className={"form-group"}>
                        <FlintTextBox
                            maxLength={state.listItem.get.SymbolLength}
                            minLength={state.listItem.get.SymbolLength}
                            required={true}
                            value={state.listItem.get.Symbol}
                            onChange={x => state.listItem.set("Symbol", x.value)}
                            label={t("COMMON_Symbol")}/>
                    </div>
                    <div className={"form-group"}>
                        <FlintTextBox
                            maxLength={100}
                            value={state.listItem.get.Name}
                            onChange={x => state.listItem.set("Name", x.value)}
                            required={true}
                            label={t("COMMON_Name")}/>
                    </div>
                    {utils.showGuideChoice && 'TypeOnlyGuideType' in state.listItem.get && 
                        <div className={"form-group d-flex justify-content-center"}>
                            <RadioGroup 
                                disabled={state.listItem.get.Id > 0}
                                layout={"horizontal"} data={utils.guideOptions}
                                onChange={x=>state.listItem.set("TypeOnlyGuideType", x.value)}
                                value={state.listItem.get.TypeOnlyGuideType}/>
                        </div>}
                    {utils.showGuideListTypeChoice && 'GuideListId' in state.listItem.get && 
                        <div className={"form-group d-flex justify-content-center"}>
                            <RadioGroup
                                disabled={state.listItem.get.Id > 0}
                                layout={"horizontal"} data={utils.guideListTypes}
                                onChange={x=>state.listItem.set("GuideListId", x.value)}
                                value={state.listItem.get.GuideListId}/>
                        </div>}
                </div>
                <div className={"dialog-actions"}>
                    <WindowActionsBar layout={"stretched"}>
                        <FlintButton themeColor={"secondary"}
                                     onClick={handlers.handleCancelled}>{t("Cancel")}</FlintButton>
                        <FlintButton themeColor={"primary"}
                                     onClick={handlers.handleSaveClicked}>{t("Save")}</FlintButton>
                    </WindowActionsBar>
                </div>
            </div>
        </FlintLoadingWrapper>
    );
}

export interface EditProductCodeListItemComponentProps extends BasePopupProps<(EmeaProductCodeListItemModel | NaProductCodeListItemModel)> {
    labelKey: string;
    listItem: (EmeaProductCodeListItemModel | NaProductCodeListItemModel);
}

function useEditProductCodeListItemComponent(props: EditProductCodeListItemComponentProps) {
    const [isLoading, setIsLoading] = useState(false);
    const [listItem, setListItem] = useState<(EmeaProductCodeListItemModel | NaProductCodeListItemModel)>(() => ({...props.listItem}));
    const {showNotification} = useFlintNotificationContext();
    const guideOptions = [
        {label: 'GUIDE', value: 'G'},
        {label: 'NON-GUIDE', value: 'NG'}
    ];
    const guideListTypes = [
        {label: 'N/A', value: 0},
        {label: 'PmsU', value: 1},
        {label: 'PmsC', value: 2},
        {label: 'PmsK', value: 4},
        {label: 'PmsN', value: 8},
    ];
    const showGuideChoice = useMemo(() => {
        return props.listItem.ProductCodeType == EnumProductCodeType.EMEA && props.listItem.ListTypeId == EnumProductCodeListTypes_Emea.Type
    }, [props.listItem])
    
    const showGuideListTypeChoice = useMemo(() => {
        return props.listItem.ProductCodeType == EnumProductCodeType.EMEA && props.listItem.ListTypeId == EnumProductCodeListTypes_Emea.GuideType
    }, [props.listItem])

    function handleSaveClicked() {
        setIsLoading(true);
        axios.post<ApiResponseDto<(EmeaProductCodeListItemModel | NaProductCodeListItemModel)>>(ApiUrl_ProductCodes_SaveListItem, listItem)
            .then(response => {
                setListItem(response.data.Result);
                props.OnConfirmedAndSaved(response.data.Result);
            }).catch(error => showNotification(error))
            .finally(() => setIsLoading(false));
    }

    function updateListItem<T extends keyof EmeaProductCodeListItemModel>(key: T, value: EmeaProductCodeListItemModel[T]): void;
    function updateListItem<T extends keyof NaProductCodeListItemModel>(key: T, value: NaProductCodeListItemModel[T]): void;
    function updateListItem(key: string, value: unknown) 
    {
        setListItem(prev => ({
            ...prev,
            [key]: value
        }));
    }



    return {
        state: {
            listItem: {get: listItem, set: updateListItem},
            isLoading: {get: isLoading},
        },
        utils: {
            showGuideChoice,
            guideOptions,
            showGuideListTypeChoice,
            guideListTypes,
            labelKey: props.labelKey,
            visibility: {},
        },
        handlers: {
            handleSaveClicked,
            handleCancelled: props.OnCancelled,
        },
    };
}
